import { LegalLayout } from "@/components/LegalLayout";

const EMAIL = "ilmuseodellepromesse@gmail.com";

const Section = ({ title, children }) => (
  <div className="space-y-4">
    <h2 className="font-serif text-2xl md:text-3xl text-museo-text mt-10">{title}</h2>
    {children}
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-2 list-none">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3">
        <span className="text-museo-accent">—</span>
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const Mail = () => (
  <a href={`mailto:${EMAIL}`} data-testid="privacy-email" className="text-museo-accent hover:text-museo-text transition-colors break-all">
    {EMAIL}
  </a>
);

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy">
      <p className="text-museo-muted/80">Ultimo aggiornamento: 24 giugno 2026</p>
      <p>
        La presente Privacy Policy descrive come vengono raccolti, utilizzati e protetti i dati personali
        degli utenti che utilizzano il sito Il Museo delle Promesse.
      </p>
      <p>
        Il Museo delle Promesse è una piattaforma digitale che permette agli utenti di acquistare uno spazio
        pubblico all'interno di un museo digitale collettivo e pubblicare una frase anonima o firmata.
      </p>
      <p>
        Per qualsiasi richiesta relativa alla privacy è possibile contattarci all'indirizzo email: <Mail />
      </p>

      <Section title="1. Titolare e contatti">
        <p>Il servizio è gestito da Il Museo delle Promesse.</p>
        <p>
          Per domande, richieste di rimozione, segnalazioni o richieste relative al trattamento dei dati
          personali, è possibile contattare il gestore del servizio all'indirizzo: <Mail />
        </p>
      </Section>

      <Section title="2. Dati raccolti">
        <p>Il sito può raccogliere e trattare le seguenti categorie di dati.</p>
        <h3 className="font-serif text-xl text-museo-text">a) Dati inseriti dall'utente</h3>
        <p>Quando un utente decide di lasciare una frase nel museo, può inserire:</p>
        <List items={[
          "il testo della frase;",
          "una firma opzionale;",
          "eventuali informazioni contenute volontariamente nella frase.",
        ]} />
        <p>La firma è facoltativa. L'utente può scegliere di restare anonimo.</p>
        <p>
          Gli utenti sono invitati a non inserire cognomi, indirizzi, numeri di telefono, email, dati sensibili
          o informazioni che possano identificare direttamente sé stessi o altre persone.
        </p>
        <h3 className="font-serif text-xl text-museo-text">b) Dati di pagamento</h3>
        <p>I pagamenti vengono gestiti tramite Stripe.</p>
        <p>Il Museo delle Promesse non conserva direttamente i dati completi della carta di pagamento dell'utente.</p>
        <p>Stripe può trattare dati necessari alla gestione del pagamento, come:</p>
        <List items={[
          "importo pagato;",
          "esito del pagamento;",
          "identificativo della transazione;",
          "dati tecnici e antifrode;",
          "eventuali informazioni richieste da Stripe per completare il pagamento.",
        ]} />
        <p>Per maggiori informazioni sul trattamento dei dati da parte di Stripe, l'utente può consultare la Privacy Policy di Stripe.</p>
        <h3 className="font-serif text-xl text-museo-text">c) Dati tecnici</h3>
        <p>Durante la navigazione il sito può raccogliere automaticamente alcuni dati tecnici, come:</p>
        <List items={[
          "indirizzo IP;",
          "tipo di dispositivo;",
          "browser utilizzato;",
          "sistema operativo;",
          "data e ora di accesso;",
          "pagine visitate;",
          "log tecnici di funzionamento e sicurezza.",
        ]} />
        <p>
          Questi dati vengono utilizzati per far funzionare correttamente il sito, prevenire abusi, proteggere
          il servizio e diagnosticare eventuali problemi tecnici.
        </p>
        <h3 className="font-serif text-xl text-museo-text">d) Dati inviati tramite contatto o segnalazione</h3>
        <p>Se l'utente invia una segnalazione o contatta il sito via email o tramite modulo, possono essere raccolti:</p>
        <List items={[
          "indirizzo email;",
          "testo del messaggio;",
          "numero della frase segnalata;",
          "motivo della segnalazione;",
          "eventuali informazioni inserite volontariamente dall'utente.",
        ]} />
      </Section>

      <Section title="3. Finalità del trattamento">
        <p>I dati vengono trattati per le seguenti finalità:</p>
        <List items={[
          "permettere la pubblicazione di una frase nel Museo delle Promesse;",
          "gestire il pagamento del servizio;",
          "assegnare un numero unico alla frase pubblicata;",
          "rendere la frase visibile pubblicamente sul sito;",
          "permettere la ricerca delle frasi pubblicate;",
          "generare una pagina pubblica permanente per la frase;",
          "gestire segnalazioni, richieste di rimozione e comunicazioni con gli utenti;",
          "moderare i contenuti e prevenire abusi;",
          "garantire la sicurezza tecnica del sito;",
          "adempiere a eventuali obblighi di legge, fiscali o contabili.",
        ]} />
      </Section>

      <Section title="4. Base giuridica del trattamento">
        <p>Il trattamento dei dati si basa, a seconda dei casi, sulle seguenti basi giuridiche:</p>
        <List items={[
          "esecuzione di un contratto, per consentire all'utente di acquistare e pubblicare una frase nel museo digitale;",
          "adempimento di obblighi legali, per eventuali obblighi fiscali, contabili o richieste delle autorità competenti;",
          "legittimo interesse, per garantire sicurezza, moderazione, prevenzione di abusi, protezione del sito e gestione delle segnalazioni;",
          "consenso, quando l'utente sceglie volontariamente di inviare dati aggiuntivi tramite contatto, segnalazione o firma opzionale.",
        ]} />
      </Section>

      <Section title="5. Pubblicazione delle frasi">
        <p>Le frasi pubblicate nel Museo delle Promesse sono visibili pubblicamente.</p>
        <p>Ogni frase può avere:</p>
        <List items={[
          "un numero progressivo;",
          "un testo;",
          "una firma opzionale;",
          "una pagina pubblica;",
          "una data di pubblicazione.",
        ]} />
        <p>Le frasi pubblicate possono essere lette da chiunque visiti il sito e possono essere condivise tramite link.</p>
        <p>L'utente è responsabile di ciò che decide di scrivere.</p>
        <p>È vietato pubblicare contenuti che includano:</p>
        <List items={[
          "dati personali di terzi;",
          "cognomi;",
          "indirizzi;",
          "numeri di telefono;",
          "email;",
          "link;",
          "insulti;",
          "bestemmie;",
          "minacce;",
          "contenuti discriminatori;",
          "contenuti illegali;",
          "contenuti offensivi;",
          "informazioni private o sensibili.",
        ]} />
        <p>
          Il gestore si riserva il diritto di rifiutare, nascondere o rimuovere contenuti che violino le regole
          del sito o che possano arrecare danno ad altre persone.
        </p>
      </Section>

      <Section title="6. Moderazione dei contenuti">
        <p>Il sito può utilizzare sistemi automatici e controlli manuali per moderare le frasi inviate dagli utenti.</p>
        <p>La moderazione può bloccare o rimuovere contenuti che contengano:</p>
        <List items={[
          "parolacce;",
          "bestemmie;",
          "insulti;",
          "minacce;",
          "link;",
          "email;",
          "numeri di telefono;",
          "dati personali;",
          "spam;",
          "contenuti offensivi o non consentiti.",
        ]} />
        <p>In alcuni casi, un contenuto può essere messo in revisione o rimosso anche dopo la pubblicazione.</p>
      </Section>

      <Section title="7. Conservazione dei dati">
        <p>I dati vengono conservati per il tempo necessario alle finalità per cui sono stati raccolti.</p>
        <p>In particolare:</p>
        <List items={[
          "le frasi pubblicate possono rimanere visibili nel museo digitale per tutta la durata del servizio;",
          "i dati relativi ai pagamenti possono essere conservati per il tempo richiesto da obblighi fiscali, contabili o legali;",
          "le segnalazioni e le comunicazioni possono essere conservate per il tempo necessario a gestire la richiesta;",
          "i log tecnici possono essere conservati per finalità di sicurezza, manutenzione e prevenzione di abusi.",
        ]} />
        <p>L'utente può richiedere la rimozione o la modifica della visibilità di una frase contattando: <Mail /></p>
        <p>La richiesta sarà valutata compatibilmente con gli obblighi legali, tecnici e di sicurezza del servizio.</p>
      </Section>

      <Section title="8. Condivisione dei dati con terzi">
        <p>I dati possono essere condivisi con fornitori tecnici necessari al funzionamento del sito, tra cui:</p>
        <List items={[
          "provider di hosting;",
          "database cloud;",
          "servizi di pagamento, come Stripe;",
          "servizi email o strumenti necessari alla gestione delle segnalazioni;",
          "eventuali fornitori tecnici incaricati della sicurezza, manutenzione o sviluppo del sito.",
        ]} />
        <p>I dati non vengono venduti a terzi.</p>
        <p>I dati possono inoltre essere comunicati ad autorità competenti quando richiesto dalla legge.</p>
      </Section>

      <Section title="9. Pagamenti tramite Stripe">
        <p>I pagamenti vengono elaborati da Stripe.</p>
        <p>Quando l'utente effettua un pagamento, viene reindirizzato o gestito attraverso sistemi sicuri di Stripe.</p>
        <p>
          Il Museo delle Promesse riceve da Stripe solo le informazioni necessarie a verificare l'esito del
          pagamento e pubblicare la frase acquistata.
        </p>
        <p>Il Museo delle Promesse non conserva i dati completi della carta di credito o debito.</p>
      </Section>

      <Section title="10. Cookie e tecnologie simili">
        <p>Il sito può utilizzare cookie tecnici o strumenti simili necessari al suo funzionamento.</p>
        <p>Questi possono servire, ad esempio, per:</p>
        <List items={[
          "mantenere attiva una sessione tecnica;",
          "gestire la sicurezza;",
          "ricordare preferenze essenziali;",
          "consentire il corretto funzionamento del pagamento;",
          "prevenire abusi o accessi non autorizzati.",
        ]} />
        <p>
          Se in futuro verranno usati cookie di analytics, marketing o profilazione, la presente informativa
          sarà aggiornata e, ove necessario, sarà richiesto il consenso dell'utente.
        </p>
      </Section>

      <Section title="11. Sicurezza">
        <p>Il Museo delle Promesse adotta misure tecniche e organizzative ragionevoli per proteggere i dati trattati.</p>
        <p>Tuttavia, nessun sistema informatico può garantire sicurezza assoluta.</p>
        <p>
          L'utente è invitato a non inserire nella propria frase dati personali, informazioni sensibili o
          riferimenti che possano identificare direttamente sé stesso o altre persone.
        </p>
      </Section>

      <Section title="12. Minori">
        <p>Il servizio non è destinato a minori di 14 anni.</p>
        <p>Gli utenti minorenni devono utilizzare il sito solo con il consenso e la supervisione di un genitore o tutore.</p>
        <p>Il sito non intende raccogliere consapevolmente dati personali di minori.</p>
        <p>
          Se un genitore o tutore ritiene che un minore abbia inserito dati personali nel sito, può contattarci
          all'indirizzo: <Mail />
        </p>
      </Section>

      <Section title="13. Diritti dell'utente">
        <p>
          L'utente può esercitare i diritti previsti dalla normativa applicabile in materia di protezione dei
          dati personali, tra cui:
        </p>
        <List items={[
          "diritto di accesso ai dati;",
          "diritto di rettifica;",
          "diritto di cancellazione;",
          "diritto di limitazione del trattamento;",
          "diritto di opposizione;",
          "diritto alla portabilità dei dati, ove applicabile;",
          "diritto di revocare il consenso, ove il trattamento si basi sul consenso;",
          "diritto di proporre reclamo all'autorità di controllo competente.",
        ]} />
        <p>Per esercitare questi diritti è possibile scrivere a: <Mail /></p>
      </Section>

      <Section title="14. Richiesta di rimozione di una frase">
        <p>Se un utente desidera chiedere la rimozione di una frase, può contattare: <Mail /></p>
        <p>Nella richiesta è consigliato indicare:</p>
        <List items={[
          "numero della frase;",
          "motivo della richiesta;",
          "eventuali informazioni utili a identificare il contenuto.",
        ]} />
        <p>
          Il gestore valuterà la richiesta e potrà rimuovere, nascondere o mantenere il contenuto in base alle
          regole del sito e agli obblighi di legge.
        </p>
      </Section>

      <Section title="15. Modifiche alla Privacy Policy">
        <p>La presente Privacy Policy può essere aggiornata nel tempo.</p>
        <p>
          In caso di modifiche rilevanti, il sito potrà pubblicare un avviso o aggiornare la data indicata
          all'inizio del documento.
        </p>
        <p>
          L'uso continuato del sito dopo l'aggiornamento della Privacy Policy implica l'accettazione della
          versione aggiornata, nei limiti consentiti dalla legge.
        </p>
      </Section>

      <Section title="16. Contatti">
        <p>
          Per qualsiasi domanda relativa alla presente Privacy Policy, alla gestione dei dati personali, alle
          segnalazioni o alle richieste di rimozione, è possibile contattare:
        </p>
        <p>
          Il Museo delle Promesse<br />
          Email: <Mail />
        </p>
      </Section>
    </LegalLayout>
  );
}
